import { Camera, X } from "lucide-react";
import { useRef, useState } from "react";

import type { Metadata } from "@/lib/head";
import { Link } from "@/lib/nav";

import { Button } from "@buildpy/ui/button";
import SectionCard from "@buildpy/ui/section-card";
import { photoPhaseOf, photoPhasesOn } from "@buildpy/core/modules/project/photo-phase";
import { recordBlockReason } from "@buildpy/core/modules/project/transitions";
import type { ProjectPhase } from "@buildpy/core/modules/project/types";
import { projectNameOf } from "@buildpy/core/modules/project/view";
import type { ProjectTodayView } from "@buildpy/core/views/g3b/project-today";
import LogComposeSheet from "@/modules/project/ui/log-compose-sheet";
import { usePhotoUpload } from "@/modules/project/ui/use-photo-upload";

export const metadata: Metadata = { title: "사진 찍기" };

const NO_TAG = "태그 없음";

interface Shots {
  count: number;
  tags: string[];
}

// 촬영 — 공정 태그를 먼저 고르고 폰 카메라로 여러 장 찍어 오늘 날짜로 올린다(시안 Camera · 네이티브 카메라 모듈 없음).
const Page = ({ data }: { data: ProjectTodayView }) => {
  const { project, phases, today, phaseDefs } = data;
  const inputRef = useRef<HTMLInputElement>(null);
  const { progress, uploadAll } = usePhotoUpload(project.id, today);
  // undefined = 아직 안 고름(기본값) · null = 「태그 없음」.
  const [choice, setChoice] = useState<string | null | undefined>(undefined);
  const [shots, setShots] = useState<Shots>({ count: 0, tags: [] });
  const [pendingCount, setPendingCount] = useState(0);

  const blockReason = recordBlockReason(project);
  const codeName = new Map(phaseDefs.map((p) => [p.code, p.name]));
  const phaseName = (phase: ProjectPhase): string => codeName.get(phase.phase_code) ?? phase.phase_code;
  const photoPhases = photoPhasesOn(phases, today, today);
  const tagOptions = photoPhases.options.map((p) => ({ id: p.id, name: phaseName(p) }));
  const { id: phaseId } = photoPhaseOf(tagOptions, choice, photoPhases.defaultId);
  const tagName = tagOptions.find((p) => p.id === phaseId)?.name ?? NO_TAG;
  const logPhaseOptions = [...phases].sort((a, b) => a.sort_order - b.sort_order).map((p) => ({ id: p.id, name: phaseName(p) }));
  const siteName = projectNameOf(project);
  const detailHref = `/app/projects/${encodeURIComponent(project.id)}`;
  const busy = progress !== null;

  const shoot = async (list: FileList | null) => {
    const picked = list?.length ?? 0;
    if (picked === 0) return;
    const tag = tagName;
    setPendingCount(picked);
    try {
      const ok = await uploadAll(list, phaseId);
      if (ok > 0) setShots((s) => ({ count: s.count + ok, tags: s.tags.includes(tag) ? s.tags : [...s.tags, tag] }));
    } finally {
      setPendingCount(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="mx-auto flex w-full min-w-0 max-w-xl flex-col gap-y-4" data-testid="project-camera">
      <header className="flex items-center gap-x-2">
        <Button asChild aria-label="촬영 닫기" size="icon" variant="ghost">
          <Link href={detailHref}>
            <X />
          </Link>
        </Button>
        <h1 className="min-w-0 truncate text-lg font-semibold">{siteName}</h1>
      </header>

      {blockReason ? (
        <SectionCard className="text-sm text-muted-foreground" data-testid="camera-blocked" padding="sm">
          {blockReason}
        </SectionCard>
      ) : (
        <>
          <div className="flex flex-col gap-y-1.5">
            <div aria-label="공정 태그" className="flex flex-wrap gap-1.5" role="radiogroup">
              {[...tagOptions, { id: "", name: NO_TAG }].map((tag) => (
                <Button
                  key={tag.id || "none"}
                  aria-checked={(phaseId ?? "") === tag.id}
                  disabled={busy}
                  role="radio"
                  size="chip"
                  type="button"
                  variant="chip"
                  onClick={() => setChoice(tag.id || null)}>
                  {tag.name}
                </Button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">고른 공정은 이번 촬영이 끝날 때까지 모든 사진에 붙어요</p>
          </div>

          <Button
            className="w-full"
            data-testid="camera-shoot"
            disabled={busy}
            size="field"
            variant="primary"
            onClick={() => inputRef.current?.click()}>
            <Camera />
            {progress ? `올리는 중 ${progress.done}/${progress.total}` : "사진 찍기"}
          </Button>

          {(busy || shots.count > 0) && (
            <SectionCard aria-label="올리기 결과" className="flex flex-col gap-y-3" data-testid="camera-result" padding="sm">
              <div className="min-w-0">
                <p className="font-semibold">
                  {busy ? `사진 ${shots.count + pendingCount}장을 올리고 있어요` : `사진 ${shots.count}장을 올렸어요`}
                </p>
                <p className="truncate text-sm text-muted-foreground">
                  {siteName} · {(busy && !shots.tags.includes(tagName) ? [...shots.tags, tagName] : shots.tags).join(", ")}
                </p>
              </div>
              {shots.count > 0 && (
                <div className="flex flex-col gap-y-2">
                  <LogComposeSheet
                    defaultPhaseId={phaseId}
                    logDate={today}
                    phases={logPhaseOptions}
                    photoNote={`방금 찍은 ${shots.count}장`}
                    projectId={project.id}
                    projectStatus={project.status}
                    trigger={
                      <Button className="w-full" data-testid="camera-log" disabled={busy} size="field" variant="primary">
                        이 사진으로 현장일지 쓰기
                      </Button>
                    }
                  />
                  <Button className="w-full" disabled={busy} size="field" variant="outline" onClick={() => inputRef.current?.click()}>
                    계속 찍기
                  </Button>
                </div>
              )}
            </SectionCard>
          )}

          <input
            ref={inputRef}
            multiple
            accept="image/*"
            aria-label="카메라로 사진 찍기"
            capture="environment"
            className="hidden"
            type="file"
            onChange={(e) => void shoot(e.target.files)}
          />
        </>
      )}
    </div>
  );
};

export default Page;
